import * as React from 'react';

import { Block, type Fragment, type Inline, type Leaf, Text } from '@gitbook/slate';

import type { EditorContainer, NodeDOMAttributes } from '../interfaces';
import { OffsetKey } from '../utils';
import { renderLeafText } from './LeafRenderer';

/**
 * Render a virtual placholder for a node.
 * The placeholder is invisible but contains the whole markup to make the browser work with it.
 */
export const NodeVirtualRenderer = React.memo(function NodeVirtualRendererMemo(props: {
    node: Block | Inline;
    editor: EditorContainer;
    height?: number | undefined;
    attributes?: NodeDOMAttributes;
    domRef?: React.RefCallback<Element>;
}): React.ReactElement {
    const { editor, node, height, domRef, attributes: attrs } = props;

    const attributes: NodeDOMAttributes = {
        'data-key': node.key,
        ...(attrs || {}),
    };

    // Render the children
    const nodes = node.nodes.map((child: Block | Inline | Text) => {
        if (Text.isText(child)) {
            return <TextVirtualRenderer key={child.key} node={child} parent={node} />;
        }

        return <NodeVirtualRenderer key={child.key} node={child} editor={editor} />;
    });

    // Render the fragments.
    const fragments = (Block.isBlock(node) && node.fragments ? node.fragments : []).reduce<
        React.ReactElement[]
    >((fragments, fragment, key) => {
        return fragments.concat(
            <FragmentVirtualRenderer key={key} fragment={fragment} editor={editor} />
        );
    }, []);

    const children = [...nodes, ...fragments];

    const style: React.CSSProperties = {
        height,
        opacity: 0,
    };

    const element = Block.isBlock(node)
        ? editor.stack.find('renderVirtualNode', {
              attributes,
              node,
              style,
              children,
          })
        : null;

    return (
        element || (
            <div {...attributes} data-virtual ref={domRef} style={style}>
                {children}
            </div>
        )
    );
});

/**
 * Render a virtual text node. Designed to render simply the leaves of the node,
 * without any decorations or selections. See the Slate TextRenderer.
 */
const TextVirtualRenderer = React.memo(function TextVirtualRendererMemo(props: {
    node: Text;
    parent: Block | Inline;
}): React.ReactElement {
    const { node, parent } = props;

    return (
        <span key={node.key} data-key={node.key}>
            {node.leaves.map((leaf, leafIndex) => (
                <LeafVirtualRenderer
                    key={`${node.key}-leaf:${leafIndex}`}
                    leaf={leaf}
                    node={node}
                    parent={parent}
                    index={leafIndex}
                />
            ))}
        </span>
    );
});

/**
 * Render a virtual fragment.
 */
const FragmentVirtualRenderer = React.memo(function FragmentVirtualRendererMemo(props: {
    fragment: Fragment;
    editor: EditorContainer;
}): React.ReactElement {
    const { fragment, editor } = props;

    return (
        <div data-key={fragment.key}>
            {fragment.nodes.map((child: Block) => {
                return <NodeVirtualRenderer key={child.key} node={child} editor={editor} />;
            })}
        </div>
    );
});

/**
 * Render a virtual leaf node. Designed to render simply the text, without
 * any decoration or selection. See the Slate LeafRenderer.
 */
const LeafVirtualRenderer = React.memo(function LeafVirtualRendererMemo(props: {
    leaf: Leaf;
    index: number;
    node: Text;
    parent: Block | Inline;
}): React.ReactElement {
    const { node, parent, index, leaf } = props;

    const offsetKey = OffsetKey.stringify({
        key: node.key,
        index,
    });

    return (
        <span key={offsetKey} data-offset-key={offsetKey} data-slate-leaf>
            {renderLeafText({
                node,
                block: parent,
                text: leaf.text,
                index,
                parent,
                leaves: node.leaves,
            })}
        </span>
    );
});
